import { FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { faChevronDown, faChevronRight } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";

import { useUpdateListOutreachConfigMutation } from "@/pages/Private/redux/list/list-outreach.api";

import { OutreachTemplateScopeArgs } from "@/pages/Private/redux/list/list-outreach-template.api";

import { InstructionEditor, InstructionPatch } from "./InstructionEditor";

interface SenderRoutingDisclosureProps {
	listId: number;
	/** The persisted free-text rules. */
	instruction: string;
	/** The sender-rule template currently loaded, for the `<name> loaded` label. */
	templateId: number | null;
	portalContainer?: HTMLElement | null;
}

/**
 * *How senders get picked if multiple senders are activated* (PREC-3687 §4.3, AC 20).
 *
 * A disclosure, not a modal: closed it states the rule in one line; open it swaps that line for an
 * instruction and reveals the editor. The rules are read at auto-generation time (block 7/8) to
 * order the pool — nothing here automates anything on its own.
 */
export const SenderRoutingDisclosure: FunctionComponent<SenderRoutingDisclosureProps> = ({
	listId,
	instruction,
	templateId,
	portalContainer,
}) => {
	const { t } = useTranslation();
	const tr = (key: string) => t(`list.outreach.chat.senderRouting.${key}`);

	const [isOpen, setIsOpen] = useState(false);
	const [updateOutreachConfig] = useUpdateListOutreachConfigMutation();

	/**
	 * Sender rules are company-wide, so the shelf carries no source type (D12). A `SENDER_ROUTING`
	 * scope with `listSourceType: null` is complete, so the templates menu fetches it normally.
	 */
	const scope: OutreachTemplateScopeArgs = useMemo(
		() => ({ kind: "SENDER_ROUTING", listSourceType: null }),
		[]
	);

	const handlePersist = async (patch: InstructionPatch) => {
		try {
			await updateOutreachConfig({
				listId,
				body: {
					...(patch.instruction === undefined
						? {}
						: { senderRoutingInstruction: patch.instruction }),
					...(patch.templateId === undefined ? {} : { senderRoutingTemplateId: patch.templateId }),
				},
			}).unwrap();
		} catch {
			// Errors surfaced by the global RTK Query error middleware.
		}
	};

	return (
		<div className="mt-4 rounded-xl border border-border-light bg-gray-50 px-3.5 py-3">
			<button
				aria-expanded={isOpen}
				className="flex w-full cursor-pointer items-center gap-2 text-left text-xs font-semibold text-text"
				type="button"
				onClick={() => setIsOpen((open) => !open)}
			>
				<Icon className="h-3 w-3 text-text-meta" icon={isOpen ? faChevronDown : faChevronRight} />
				{tr("title")}
			</button>
			{/* The summary line is the rule when closed and the instruction when open — the mockup
			    swaps it deliberately, so the collapsed state still says something useful. */}
			<p className="mt-1.5 pl-5 text-xs leading-5 text-text-meta">
				{isOpen ? tr("openHint") : tr("summary")}
			</p>
			{isOpen && (
				<div className="mt-3">
					<InstructionEditor
						loadedTemplateId={templateId}
						placeholder={tr("placeholder")}
						portalContainer={portalContainer}
						saveScopeHint={tr("scopeHintCompanyWide")}
						scope={scope}
						value={instruction}
						// `variableGroups` is deliberately omitted: sender rules have no variable
						// catalogue, and omitting the prop hides the Variables dropdown entirely.
						onPersist={handlePersist}
					/>
				</div>
			)}
		</div>
	);
};
